import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import Alert from "./Alert";

const MAX_ALERTS = 4;
const DEFAULT_DURATION_MS = 5000;
const DEDUPE_WINDOW_MS = 2500;

const defaultTitles = {
  success: "Success",
  error: "Action failed",
  warning: "Attention needed",
  info: "Notice",
};

const AlertContext = createContext(null);

const createAlertId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeMessage = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return value.message?.trim() || "Something went wrong.";
  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message.trim();
    if (typeof value.error === "string") return value.error.trim();
  }
  return "";
};

const inferAlertType = (message = "") => {
  const value = message.toLowerCase();
  const successSignals = ["success", "saved", "updated", "created", "completed", "done", "verified", "submitted", "deleted", "synced", "✅"];
  if (successSignals.some((signal) => value.includes(signal))) return "success";
  return "error";
};

const useAlertTimers = () => {
  const timersRef = useRef(new Map());

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const registerTimer = useCallback((id, callback, delay) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(id);
      callback();
    }, delay);
    timersRef.current.set(id, timer);
  }, []);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  return { clearTimer, registerTimer };
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const seenRef = useRef(new Map());
  const { clearTimer, registerTimer } = useAlertTimers();

  const dismiss = useCallback(
    (id) => {
      clearTimer(id);
      setAlerts((prev) => prev.filter((item) => item.id !== id));
    },
    [clearTimer]
  );

  const notify = useCallback(
    ({ type = "info", title, message, duration = DEFAULT_DURATION_MS, sticky = false, id }) => {
      const normalized = normalizeMessage(message);
      if (!normalized) return null;

      const alertType = ["success", "error", "warning", "info"].includes(type) ? type : "info";
      const dedupeKey = `${alertType}:${title || ""}:${normalized}`;
      const now = Date.now();
      const lastSeen = seenRef.current.get(dedupeKey);
      if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
        return null;
      }
      seenRef.current.set(dedupeKey, now);

      const alertId = id || createAlertId();
      const nextAlert = {
        id: alertId,
        type: alertType,
        title: title || defaultTitles[alertType],
        message: normalized,
      };

      setAlerts((prev) => {
        const next = [...prev, nextAlert];
        if (next.length <= MAX_ALERTS) return next;

        const overflow = next.length - MAX_ALERTS;
        const trimmed = next.slice(overflow);
        next.slice(0, overflow).forEach((item) => clearTimer(item.id));
        return trimmed;
      });

      if (!sticky && duration > 0) {
        registerTimer(alertId, () => dismiss(alertId), duration);
      }

      return alertId;
    },
    [clearTimer, dismiss, registerTimer]
  );

  const success = useCallback(
    (message, options = {}) =>
      notify({
        type: "success",
        message,
        ...options,
      }),
    [notify]
  );

  const error = useCallback(
    (message, options = {}) =>
      notify({
        type: "error",
        message,
        ...options,
      }),
    [notify]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const nativeAlert = window.alert;
    window.alert = (value) => {
      const message = normalizeMessage(value);
      if (!message) return;
      const type = inferAlertType(message);
      notify({
        type,
        title: defaultTitles[type],
        message,
      });
    };

    return () => {
      window.alert = nativeAlert;
    };
  }, [notify]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onError = (event) => {
      const message =
        normalizeMessage(event?.error) ||
        normalizeMessage(event?.message) ||
        "An unexpected error occurred.";
      error(message, { title: "Unexpected error" });
    };

    const onUnhandledRejection = (event) => {
      const message =
        normalizeMessage(event?.reason) ||
        "An unexpected error occurred. Please try again.";
      error(message, { title: "Unexpected error" });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [error]);

  const value = useMemo(
    () => ({
      notify,
      success,
      error,
      dismiss,
    }),
    [notify, success, error, dismiss]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed right-4 top-4 z-[1200] flex w-[min(28rem,calc(100vw-2rem))] flex-col gap-3"
        aria-live="polite"
        aria-atomic="false"
      >
        <AnimatePresence initial={false}>
          {alerts.map((alert) => (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 18, scale: 0.98 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="pointer-events-auto"
            >
              <Alert
                type={alert.type}
                title={alert.title}
                message={alert.message}
                onClose={() => dismiss(alert.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </AlertContext.Provider>
  );
};

AlertProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within AlertProvider.");
  }
  return context;
};

